import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  ROLES,
  ROLE_NAMES,
  ROLE_HELP,
  permissionsForRole,
} from "../auth/permissions";
import { BASE_SPACE_URL } from "../sampleData";
import "../users-redesign.css";

/* ------------------------------------------------------------------ *
 * UsersDirectory — the people list + editor shared by
 *   /users            (one business, inside the AppShell)   mode="business"
 *   /overview/users   (every business, on the overview)     mode="group"
 *
 * The page owns fetching and the API calls; this component only renders
 * and calls back. Everything it needs is normalised by the page into:
 *
 *   businesses: [{ id, name, code, can_manage }]
 *   users:      [{ id, full_name, email, userName, thumb, active, date,
 *                  memberships: [{ business_id, role, permissions,
 *                                  custom_permissions, created_at }] }]
 *   api:        { add(businessId, detail), edit(businessId, id, updateData),
 *                 remove(businessId, id) }
 *   grantableIn(businessId) -> the caller's own permission set there — the
 *               server refuses to hand out a permission the caller lacks,
 *               so those switches are shown locked.
 *
 * ACCESS MODEL. A membership's role gives it a default permission set. The
 * editor only picks the role: an untouched membership keeps whatever set
 * the server holds (including a pinned "custom" one), and changing the role
 * resets it to that role's defaults (`permissions: null` clears the pin).
 *
 * The business page is one table grouped by role — Owners, Admins,
 * Florists, Drivers. The group page puts owners (of any business in
 * scope) in one full-width list at the top, then one board per business
 * below it, each grouped by role; a person can sit on several boards.
 * ------------------------------------------------------------------ */

const ROLE_FILTERS = [
  { id: "all", label: "Everyone" },
  ...ROLES.map((r) => ({ id: r, label: `${ROLE_NAMES[r]}s` })),
];

const sameSet = (a, b) =>
  a.length === b.length && a.every((p) => b.includes(p));

/* Custom = the held set differs from what the role would grant. */
const isCustom = (entry) => !sameSet(entry.permissions, permissionsForRole(entry.role));

const AVATAR_COLORS = ["var(--rf-brand)", "var(--rf-accent)", "#7a5b7d", "#6b6f3f", "#a8483e", "#4d6a8a"];
function avatarColor(key) {
  let h = 0;
  for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name, fallback) {
  const words = String(name || fallback || "")
    .split(/[\s_.-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

const displayName = (u) => (u && (u.full_name || u.userName)) || "—";

function Avatar({ user, large }) {
  const cls = `us-avatar${large ? " is-lg" : ""}`;
  if (user && user.thumb) {
    return (
      <span className={cls}>
        <img src={`${BASE_SPACE_URL}${user.thumb}`} alt="" />
      </span>
    );
  }
  return (
    <span className={cls} style={{ background: avatarColor(user && user.userName) }}>
      {initials(user && user.full_name, user && user.userName)}
    </span>
  );
}

function RolePill({ role, code, readOnly }) {
  return (
    <span className={`us-pill role-${role || "none"}${readOnly ? " is-readonly" : ""}`}>
      <i />
      {code && (
        <>
          <span className="us-pill-code">{code}</span>
          <span className="us-pill-sep">·</span>
        </>
      )}
      {ROLE_NAMES[role] || role || "No role"}
    </span>
  );
}

/* ---------------- role picker (one membership) ---------------- */

function RolePicker({ entry, onChange, grantable, readOnly, isSelf }) {
  return (
    <div className="us-access">
      <div className="us-section-title">Role</div>
      <div className="us-roles">
        {ROLES.map((r) => {
          // The server refuses to hand out a permission the caller lacks,
          // so a role that carries one can't be picked.
          const cannotGrant =
            entry.role !== r && permissionsForRole(r).some((p) => !grantable.includes(p));
          let title;
          if (isSelf) title = "You can't change your own role";
          else if (cannotGrant) title = "This role has access you don't hold yourself";
          return (
            <button
              key={r}
              type="button"
              className={`us-role${entry.role === r ? " is-active" : ""}`}
              disabled={readOnly || isSelf || cannotGrant}
              title={title}
              onClick={() => onChange({ ...entry, role: r, permissions: permissionsForRole(r) })}
            >
              <span className="us-role-name">{ROLE_NAMES[r]}</span>
              <span className="us-role-help">{ROLE_HELP[r]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- editor modal ---------------- */

function UserEditor({ mode, businesses, user, currentUser, grantableIn, api, onClose, onSaved }) {
  const bizById = useMemo(() => new Map(businesses.map((b) => [String(b.id), b])), [businesses]);
  const manageable = useMemo(() => businesses.filter((b) => b.can_manage), [businesses]);
  const adding = !user;
  const isSelf = Boolean(
    user && currentUser && String(user.id) === String(currentUser.id)
  );

  const [fullName, setFullName] = useState(user ? user.full_name || "" : "");
  const [email, setEmail] = useState(user ? user.email || "" : "");
  const [userName, setUserName] = useState(user ? user.userName || "" : "");
  const [userNameTouched, setUserNameTouched] = useState(Boolean(user));
  const [password, setPassword] = useState("");

  /* One entry per membership being edited. `original` is what the server
     currently has, so Save only sends what changed. */
  const [entries, setEntries] = useState(() => {
    if (user) {
      return (user.memberships || []).map((m) => ({
        business_id: String(m.business_id),
        role: m.role,
        permissions: [...(m.permissions || permissionsForRole(m.role))],
        isNew: false,
        original: {
          role: m.role,
          permissions: [...(m.permissions || permissionsForRole(m.role))],
          custom: m.custom_permissions !== null && m.custom_permissions !== undefined,
        },
      }));
    }
    const first = manageable[0] || businesses[0];
    return first
      ? [{ business_id: String(first.id), role: "staff", permissions: permissionsForRole("staff"), isNew: true }]
      : [];
  });
  const [activeBiz, setActiveBiz] = useState(() =>
    entries.length ? entries[0].business_id : null
  );
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const active = entries.find((e) => e.business_id === activeBiz) || null;
  const activeBizRow = active ? bizById.get(active.business_id) : null;
  const canManageActive = Boolean(activeBizRow && activeBizRow.can_manage);
  const canManageAny = entries.some((e) => (bizById.get(e.business_id) || {}).can_manage);
  const readOnly = adding ? manageable.length === 0 : !canManageAny;

  const updateEntry = (next) =>
    setEntries((prev) => prev.map((e) => (e.business_id === next.business_id ? next : e)));

  // Group page, adding: the business picker moves the single entry.
  const moveEntryTo = (bizId) => {
    setEntries((prev) => prev.map((e) => ({ ...e, business_id: String(bizId) })));
    setActiveBiz(String(bizId));
  };

  const notMemberOf = manageable.filter(
    (b) => !entries.some((e) => e.business_id === String(b.id))
  );
  const addMembership = (bizId) => {
    if (!bizId) return;
    setEntries((prev) => [
      ...prev,
      { business_id: String(bizId), role: "staff", permissions: permissionsForRole("staff"), isNew: true },
    ]);
    setActiveBiz(String(bizId));
  };
  const dropNewMembership = (bizId) => {
    setEntries((prev) => prev.filter((e) => e.business_id !== bizId));
    setActiveBiz(entries[0] ? entries[0].business_id : null);
  };

  function onNameChange(value) {
    setFullName(value);
    if (!userNameTouched) {
      setUserName(value.toLowerCase().trim().replace(/\s+/g, "_"));
    }
  }

  const permissionsPayload = (entry) => (isCustom(entry) ? entry.permissions : undefined);

  async function save() {
    const name = fullName.trim();
    const uname = userName.trim();
    const mail = email.trim();
    if (!uname) {
      toast.error("A username is required");
      return;
    }
    if (mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      toast.error("That email address doesn't look right");
      return;
    }
    if (adding && password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!adding && password && password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      if (adding) {
        const entry = entries[0];
        await api.add(entry.business_id, {
          full_name: name,
          email: mail,
          userName: uname,
          password,
          role: entry.role,
          permissions: permissionsPayload(entry),
        });
        toast.success(`${name || uname} added`);
      } else {
        const profile = {};
        if (name !== (user.full_name || "")) profile.full_name = name;
        if (mail !== (user.email || "")) profile.email = mail;
        if (password) profile.password = password;
        let profileSent = false;

        for (const entry of entries) {
          const biz = bizById.get(entry.business_id);
          if (!biz || !biz.can_manage) continue;
          if (entry.isNew) {
            // The server reuses the existing account by username and only
            // creates the membership — no password needed.
            await api.add(entry.business_id, {
              full_name: name || user.full_name,
              userName: user.userName,
              role: entry.role,
              permissions: permissionsPayload(entry),
            });
            continue;
          }
          const update = {};
          if (entry.role !== entry.original.role) update.role = entry.role;
          const custom = isCustom(entry);
          if (
            custom !== entry.original.custom ||
            !sameSet(entry.permissions, entry.original.permissions)
          ) {
            update.permissions = custom ? entry.permissions : null;
          }
          // Profile fields live on the account, not the membership — send
          // them once, with whichever manageable membership comes first.
          if (!profileSent && Object.keys(profile).length) {
            Object.assign(update, profile);
            profileSent = true;
          }
          if (Object.keys(update).length) await api.edit(entry.business_id, user.id, update);
        }
        toast.success(`${name || user.userName} saved`);
      }
      onSaved();
    } catch (err) {
      toast.error(
        (err && err.response && err.response.data && err.response.data.message) ||
          "Could not save this user"
      );
    } finally {
      setSaving(false);
    }
  }

  /* Blocking sits on the account, not a membership: the person can't sign
     in anywhere until unblocked. The server refuses blocking yourself, and
     an owner can only be blocked by another owner. */
  const blocked = Boolean(user && user.active === false);
  const callerRoleIn = (bizId) => {
    const mine = (currentUser && currentUser.memberships) || [];
    const m = mine.find((x) => String(x.business_id) === String(bizId));
    return (m && m.role) || (currentUser && (currentUser.role || currentUser.access));
  };
  const canBlock =
    !adding && active && !active.isNew && canManageActive && !isSelf &&
    (active.original.role !== "owner" || callerRoleIn(active.business_id) === "owner");

  async function setBlocked(next) {
    if (!active || !user) return;
    setSaving(true);
    try {
      await api.edit(active.business_id, user.id, { active: !next });
      toast.success(`${displayName(user)} ${next ? "blocked" : "unblocked"}`);
      onSaved();
    } catch (err) {
      toast.error(
        (err && err.response && err.response.data && err.response.data.message) ||
          `Could not ${next ? "block" : "unblock"} this user`
      );
      setSaving(false);
    }
  }

  async function remove() {
    if (!active || !user) return;
    setSaving(true);
    try {
      await api.remove(active.business_id, user.id);
      toast.success(`${displayName(user)} removed from ${bizName(activeBizRow)}`);
      onSaved();
    } catch (err) {
      toast.error(
        (err && err.response && err.response.data && err.response.data.message) ||
          "Could not remove this user"
      );
      setSaving(false);
    }
  }

  const bizName = (b) => (b && (b.name || b.code)) || "this business";
  const showBizTabs = mode === "group" && !adding;
  const showBizPicker = mode === "group" && adding && manageable.length > 1;

  return (
    <div className="us-drawer-wrap" role="dialog" aria-modal="true">
      <div className="us-scrim" onClick={onClose} />
      <div className="us-drawer">
        <header className="us-drawer-head">
          <Avatar
            user={{ full_name: fullName, userName, thumb: user && user.thumb }}
            large
          />
          <div style={{ minWidth: 0 }}>
            <h2 className="us-serif us-drawer-title">
              {adding ? "Add a person" : displayName({ full_name: fullName, userName })}
            </h2>
            <div className="us-drawer-sub">
              {adding
                ? mode === "group"
                  ? "They get access to the business you pick below."
                  : `They get access to ${bizName(businesses[0])}.`
                : readOnly
                ? "View only — you can't manage users here."
                : isSelf
                ? "This is your own account."
                : `@${user.userName}`}
            </div>
          </div>
          <button type="button" className="us-drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="us-drawer-body">
          {blocked && (
            <div className="us-blocked-note">
              <b>Blocked.</b> {displayName(user)} can't sign in to any business
              until they are unblocked.
            </div>
          )}
          <section>
            <div className="us-section-title">Profile</div>
            <div className="us-fields">
              <div className="us-field">
                <label className="us-field-label" htmlFor="us-full-name">Full name</label>
                <input
                  id="us-full-name"
                  className="us-input"
                  value={fullName}
                  disabled={readOnly}
                  placeholder="e.g. Ray Milano"
                  onChange={(e) => onNameChange(e.target.value)}
                />
              </div>
              <div className="us-field">
                <label className="us-field-label" htmlFor="us-username">Username</label>
                <input
                  id="us-username"
                  className="us-input"
                  value={userName}
                  disabled={readOnly || !adding}
                  placeholder="e.g. ray_milano"
                  autoCapitalize="none"
                  onChange={(e) => {
                    setUserNameTouched(true);
                    setUserName(e.target.value);
                  }}
                />
                {!adding && <span className="us-field-hint">Usernames can't be changed.</span>}
              </div>
              <div className="us-field is-wide">
                <label className="us-field-label" htmlFor="us-email">Email</label>
                <input
                  id="us-email"
                  className="us-input"
                  type="email"
                  value={email}
                  disabled={readOnly}
                  autoComplete="off"
                  placeholder="e.g. ray@raysflorist.com.au"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="us-field is-wide">
                <label className="us-field-label" htmlFor="us-password">
                  {adding ? "Password" : "New password"}
                </label>
                <input
                  id="us-password"
                  className="us-input"
                  type="password"
                  value={password}
                  disabled={readOnly}
                  autoComplete="new-password"
                  placeholder={adding ? "At least 8 characters" : "Leave blank to keep the current one"}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {showBizPicker && (
                <div className="us-field is-wide">
                  <label className="us-field-label" htmlFor="us-biz">Business</label>
                  <select
                    id="us-biz"
                    className="us-select"
                    value={activeBiz || ""}
                    onChange={(e) => moveEntryTo(e.target.value)}
                  >
                    {manageable.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}{b.code ? ` (${b.code})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>

          <section>
            {showBizTabs && (
              <div className="us-biztabs">
                {entries.map((e) => {
                  const b = bizById.get(e.business_id);
                  return (
                    <button
                      key={e.business_id}
                      type="button"
                      className={`us-biztab${e.business_id === activeBiz ? " is-active" : ""}${e.isNew ? " is-new" : ""}`}
                      onClick={() => setActiveBiz(e.business_id)}
                    >
                      {(b && (b.code || b.name)) || "?"}
                      <span className="us-biztab-role">{ROLE_NAMES[e.role]}</span>
                    </button>
                  );
                })}
                {notMemberOf.length > 0 && (
                  <select
                    className="us-select"
                    value=""
                    aria-label="Add to another business"
                    onChange={(e) => addMembership(e.target.value)}
                  >
                    <option value="">+ Add to business…</option>
                    {notMemberOf.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {active ? (
              <>
                {!canManageActive && !adding && (
                  <div className="us-readonly-note">
                    You can see this membership but can't change it — you don't
                    manage users in {bizName(activeBizRow)}.
                  </div>
                )}
                {active.isNew && !adding && (
                  <div className="us-readonly-note">
                    Adding {displayName(user)} to {bizName(activeBizRow)}.{" "}
                    <button
                      type="button"
                      className="us-link"
                      onClick={() => dropNewMembership(active.business_id)}
                    >
                      Undo
                    </button>
                  </div>
                )}
                <RolePicker
                  entry={active}
                  onChange={updateEntry}
                  grantable={grantableIn(active.business_id)}
                  readOnly={readOnly || !canManageActive}
                  isSelf={isSelf}
                />
              </>
            ) : (
              <div className="us-readonly-note">No business to add this person to.</div>
            )}
          </section>
        </div>

        <footer className="us-drawer-foot">
          {confirmBlock && (
            <div className="us-confirm">
              <span>
                Block <b>{displayName(user)}</b>? They'll be signed out and
                can't log in again until unblocked.
              </span>
              <button type="button" className="us-btn us-btn-ghost" onClick={() => setConfirmBlock(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="us-btn us-btn-danger" onClick={() => setBlocked(true)} disabled={saving}>
                Block
              </button>
            </div>
          )}
          {!confirmBlock && !adding && active && !active.isNew && canManageActive && !isSelf && (
            confirmRemove ? (
              <div className="us-confirm">
                <span>
                  Remove <b>{displayName(user)}</b> from {bizName(activeBizRow)}?
                </span>
                <button type="button" className="us-btn us-btn-ghost" onClick={() => setConfirmRemove(false)} disabled={saving}>
                  Keep
                </button>
                <button type="button" className="us-btn us-btn-danger" onClick={remove} disabled={saving}>
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="us-btn us-btn-danger"
                onClick={() => setConfirmRemove(true)}
                disabled={saving}
              >
                Remove from {activeBizRow && activeBizRow.code ? activeBizRow.code : "business"}
              </button>
            )
          )}
          {!confirmRemove && !confirmBlock && canBlock && (
            <button
              type="button"
              className={`us-btn ${blocked ? "us-btn-ghost" : "us-btn-danger"}`}
              onClick={() => (blocked ? setBlocked(false) : setConfirmBlock(true))}
              disabled={saving}
            >
              {blocked ? "Unblock" : "Block"}
            </button>
          )}
          {!confirmRemove && !confirmBlock && (
            <>
              <span className="us-spacer" />
              <button type="button" className="us-btn us-btn-ghost" onClick={onClose} disabled={saving}>
                {readOnly ? "Close" : "Cancel"}
              </button>
              {!readOnly && (
                <button type="button" className="us-btn us-btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : adding ? "Add person" : "Save changes"}
                </button>
              )}
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

/* ---------------- directory ---------------- */

/* One person, one line. `membership` narrows the row to a single business
   (the per-business boards on the group page); without it the row shows
   every membership the person holds, as the flat table always has. */
function PersonRow({ user: u, mode, membership, bizById, self, onClick }) {
  const ms = u.memberships || [];
  const first = membership || ms[0] || {};
  const manageableHere = membership
    ? Boolean((bizById.get(String(membership.business_id)) || {}).can_manage)
    : ms.some((m) => (bizById.get(String(m.business_id)) || {}).can_manage);
  return (
    <button
      type="button"
      className={`us-row${self ? " is-self" : ""}${u.active === false ? " is-blocked" : ""}`}
      onClick={onClick}
    >
      <span className="us-col-person">
        <Avatar user={u} />
        <span className="us-person">
          <span className="us-person-name">
            {displayName(u)}
            {self && <span className="us-you">you</span>}
            {u.active === false && <span className="us-inactive">blocked</span>}
          </span>
          <span className="us-person-sub">
            @{u.userName}
            {u.email && <span className="us-person-email">{u.email}</span>}
          </span>
        </span>
      </span>
      {mode === "group" && !membership ? (
        <span className="us-col-biz">
          {ms.map((m) => {
            const b = bizById.get(String(m.business_id));
            return (
              <RolePill
                key={m.business_id}
                role={m.role}
                code={(b && (b.code || b.name)) || "?"}
                readOnly={!(b && b.can_manage)}
              />
            );
          })}
        </span>
      ) : (
        <span className="us-col-role">
          <RolePill role={first.role} />
        </span>
      )}
      <span className="us-col-date us-num">
        {fmtDate(mode === "group" ? first.created_at || u.date : u.date)}
      </span>
      <span className="us-col-actions">
        <span className="us-btn us-btn-ghost" style={{ display: "inline-flex", alignItems: "center" }}>
          {manageableHere ? "Edit" : "View"}
        </span>
      </span>
    </button>
  );
}

/* Role buckets, most senior first, from a list of [user, membership]. */
function bucketByRole(pairs, roles) {
  const buckets = new Map(roles.map((r) => [r, []]));
  const other = [];
  pairs.forEach((pair) => (buckets.get(pair.role) || other).push(pair));
  const out = roles.map((r) => ({ id: r, label: `${ROLE_NAMES[r]}s`, items: buckets.get(r) }));
  out.push({ id: "none", label: "No role", items: other });
  return out.filter((g) => g.items.length > 0);
}

function RoleGroups({ groups, mode, bizById, isSelf, onEdit }) {
  return groups.map((g) => (
    <div key={g.id} className="us-group" role="group" aria-label={g.label}>
      <div className={`us-group-head role-${g.id}`}>
        <i />
        <span className="us-group-name">{g.label}</span>
        <span className="us-group-count us-num">{g.items.length}</span>
      </div>
      {g.items.map(({ user: u, membership }) => (
        <PersonRow
          key={u.id}
          user={u}
          mode={mode}
          membership={membership}
          bizById={bizById}
          self={isSelf(u)}
          onClick={() => onEdit(u)}
        />
      ))}
    </div>
  ));
}

export default function UsersDirectory({
  mode = "business",
  title = "Users",
  lede,
  businesses = [],
  users = [],
  loading = false,
  error = null,
  reload,
  currentUser,
  grantableIn,
  api,
  standalone = false,
}) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [bizFilter, setBizFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | user

  const bizById = useMemo(() => new Map(businesses.map((b) => [String(b.id), b])), [businesses]);
  const canAdd = businesses.some((b) => b.can_manage);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !`${u.full_name || ""} ${u.userName || ""} ${u.email || ""}`.toLowerCase().includes(q)) return false;
      const ms = u.memberships || [];
      if (bizFilter !== "all" && !ms.some((m) => String(m.business_id) === bizFilter)) return false;
      if (roleFilter !== "all") {
        const inScope = bizFilter === "all" ? ms : ms.filter((m) => String(m.business_id) === bizFilter);
        if (!inScope.some((m) => m.role === roleFilter)) return false;
      }
      return true;
    });
  }, [users, query, roleFilter, bizFilter]);

  /* Business page: rows bucketed by role, most senior first. */
  const groups = useMemo(() => {
    if (mode === "group") return [];
    return bucketByRole(
      rows.map((u) => {
        const ms = u.memberships || [];
        const m = (roleFilter !== "all" && ms.find((x) => x.role === roleFilter)) || ms[0] || {};
        return { user: u, membership: null, role: m.role };
      }),
      ROLES
    );
  }, [mode, rows, roleFilter]);

  /* Group page: owners of any business in scope sit in one full-width list
     at the top; everyone else is boarded by business, and within each
     business by role. A person can appear on several boards (one per
     business they belong to), but never on a board AND in the owners list. */
  const boards = useMemo(() => {
    if (mode !== "group") return null;
    const scoped = bizFilter === "all" ? businesses : businesses.filter((b) => String(b.id) === bizFilter);
    const inScope = (u) =>
      (u.memberships || []).filter((m) => bizFilter === "all" || String(m.business_id) === bizFilter);
    const ownsSomething = (u) => inScope(u).some((m) => m.role === "owner");

    const owners = roleFilter === "all" || roleFilter === "owner" ? rows.filter(ownsSomething) : [];
    const perBiz =
      roleFilter === "owner"
        ? []
        : scoped.map((b) => {
            const pairs = [];
            rows.forEach((u) => {
              if (ownsSomething(u)) return;
              const m = (u.memberships || []).find((x) => String(x.business_id) === String(b.id));
              if (!m) return;
              if (roleFilter !== "all" && m.role !== roleFilter) return;
              pairs.push({ user: u, membership: m, role: m.role });
            });
            return { biz: b, count: pairs.length, groups: bucketByRole(pairs, ROLES.filter((r) => r !== "owner")) };
          });
    return { owners, perBiz };
  }, [mode, rows, businesses, roleFilter, bizFilter]);

  const isSelf = (u) => currentUser && String(u.id) === String(currentUser.id);

  const closeEditor = () => setEditing(null);
  const onSaved = () => {
    setEditing(null);
    if (reload) reload();
  };

  const skeleton = (n) =>
    Array.from({ length: n }, (_, i) => (
      <div key={i} className="us-skel">
        <i className="us-skel-avatar" />
        <i style={{ width: 160 + (i % 2) * 40 }} />
        <i style={{ width: 90, marginLeft: "auto" }} />
        <i style={{ width: 70 }} />
      </div>
    ));

  const emptyNote = (bold, sub) => (
    <div className="us-empty">
      <b>{bold}</b>
      {sub}
    </div>
  );

  const showSkeleton = loading && users.length === 0;
  const nothingAtAll = !loading && !error && rows.length === 0;

  return (
    <div className={`us-page${standalone ? " is-standalone" : ""}`}>
      <header className="us-head">
        <div className="us-head-text">
          <h1 className="us-serif us-title">{title}</h1>
          {lede && <p className="us-lede">{lede}</p>}
        </div>
        {canAdd && (
          <button type="button" className="us-btn us-btn-primary" onClick={() => setEditing("new")}>
            + Add person
          </button>
        )}
      </header>

      <div className="us-toolbar">
        <label className="us-search">
          <span className="us-search-icon" />
          <input
            type="search"
            placeholder="Search by name, username or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="us-segmented" role="tablist" aria-label="Filter by role">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={roleFilter === f.id ? "is-active" : ""}
              onClick={() => setRoleFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {mode === "group" && businesses.length > 1 && (
          <select
            className="us-select"
            value={bizFilter}
            aria-label="Filter by business"
            onChange={(e) => setBizFilter(e.target.value)}
          >
            <option value="all">All businesses</option>
            {businesses.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        <span className="us-spacer" />
        <span className="us-count us-num">
          {loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "person" : "people"}`}
        </span>
      </div>

      {error && (
        <div className="us-error">
          <b>Couldn't load users.</b>
          <span>{error}</span>
          <span className="us-spacer" />
          {reload && (
            <button type="button" className="us-btn us-btn-ghost" onClick={reload}>
              Try again
            </button>
          )}
        </div>
      )}

      {boards ? (
        /* ---- group page: owners on top, then one board per business ---- */
        <div className="us-boards">
          {(roleFilter === "all" || roleFilter === "owner") && (
            <section className="us-table us-board us-board-owners" aria-label="Owners">
              <div className="us-board-head role-owner">
                <i />
                <span className="us-serif us-board-title">Owners</span>
                <span className="us-board-sub">Across the group</span>
                <span className="us-spacer" />
                <span className="us-group-count us-num">{boards.owners.length}</span>
              </div>
              <div className="us-thead">
                <span className="us-col-person">Person</span>
                <span className="us-col-biz">Businesses · role</span>
                <span className="us-col-date">Joined</span>
                <span className="us-col-actions" />
              </div>
              <div className="us-tbody">
                {showSkeleton && skeleton(2)}
                {!showSkeleton && !error && boards.owners.length === 0 &&
                  emptyNote(users.length === 0 ? "No owners yet" : "No matches", "Owners of any business appear here.")}
                {boards.owners.map((u) => (
                  <PersonRow
                    key={u.id}
                    user={u}
                    mode="group"
                    membership={null}
                    bizById={bizById}
                    self={isSelf(u)}
                    onClick={() => setEditing(u)}
                  />
                ))}
              </div>
            </section>
          )}

          {boards.perBiz.length > 0 && (
            <div className="us-biz-grid" style={{ "--us-boards": Math.min(boards.perBiz.length, 3) }}>
              {boards.perBiz.map(({ biz, count, groups: bizGroups }) => (
                <section key={biz.id} className="us-table us-board" aria-label={biz.name || biz.code}>
                  <div className="us-board-head">
                    <span className="us-board-code">{biz.code || (biz.name || "?").slice(0, 3).toUpperCase()}</span>
                    <span className="us-serif us-board-title">{biz.name || biz.code}</span>
                    {!biz.can_manage && <span className="us-board-sub">View only</span>}
                    <span className="us-spacer" />
                    <span className="us-group-count us-num">{count}</span>
                  </div>
                  <div className="us-thead">
                    <span className="us-col-person">Person</span>
                    <span className="us-col-role">Role</span>
                    <span className="us-col-date">Joined</span>
                    <span className="us-col-actions" />
                  </div>
                  <div className="us-tbody">
                    {showSkeleton && skeleton(3)}
                    {!showSkeleton && !error && count === 0 &&
                      emptyNote(
                        users.length === 0 ? "No one here yet" : "No matches",
                        users.length === 0
                          ? biz.can_manage
                            ? "Add the first person with the button above."
                            : "People with access will appear here."
                          : "Try a different name or filter."
                      )}
                    <RoleGroups groups={bizGroups} mode="group" bizById={bizById} isSelf={isSelf} onEdit={setEditing} />
                  </div>
                </section>
              ))}
            </div>
          )}

          {!showSkeleton && !error && businesses.length === 0 && emptyNote("No businesses", "You don't belong to any business yet.")}
        </div>
      ) : (
        /* ---- business page: one table, grouped by role ---- */
        <div className="us-tablewrap">
          <div className="us-table">
            <div className="us-thead">
              <span className="us-col-person">Person</span>
              <span className="us-col-role">Role</span>
              <span className="us-col-date">Added</span>
              <span className="us-col-actions" />
            </div>
            <div className="us-tbody">
              {showSkeleton && skeleton(4)}
              {nothingAtAll &&
                emptyNote(
                  users.length === 0 ? "No one here yet" : "No matches",
                  users.length === 0
                    ? canAdd
                      ? "Add the first person with the button above."
                      : "People with access will appear here."
                    : "Try a different name or filter."
                )}
              <RoleGroups groups={groups} mode={mode} bizById={bizById} isSelf={isSelf} onEdit={setEditing} />
            </div>
          </div>
        </div>
      )}

      {editing && (
        <UserEditor
          key={editing === "new" ? "new" : editing.id}
          mode={mode}
          businesses={businesses}
          user={editing === "new" ? null : editing}
          currentUser={currentUser}
          grantableIn={grantableIn}
          api={api}
          onClose={closeEditor}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

export { displayName, fmtDate };
